import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import operatorsData from '../../../data/operators.json';
import { BENEFICIARY_FIELDS, MERCHANT_FIELDS, IDENTIFIER_FIELDS, SYSTEM_FIELDS } from './constants';
import { fmtAmt, fmtDate, fmtVal, humanLabel, highlight, statusColors, buildLifecycle } from './formatters';
import { Card, SectionTitle, KVRow, RoutingAttempts, StatusCheckResult,
         IdIcon, MerchantIcon, BeneIcon, DetailIcon, CommIcon, RefundIcon,
         P2PIcon, LifecycleIcon, RoutingIcon, MetaIcon,
         TxnChipIcon, StatusChipIcon, CodeChipIcon, MsgChipIcon } from './components';
import c from './styles';

const P2P_OBJ = new Set(['sender','receiver']);

// Flat code → name map built from all categories in operators.json
const OPERATOR_MAP = Object.values(operatorsData)
  .flat()
  .reduce((acc, { code, name }) => { acc[code.toUpperCase()] = name; return acc; }, {});

export default function DetailView({
  data,
  isMobile,
  handleCopy,
  copied,
  // status check
  showStatusCheck,
  scLoading,
  scResult,
  scError,
  scType,
  scProvider,
  scJsonOpen,
  setScJsonOpen,
  onStatusCheck,
  // service-specific extra fields
  metaBeneExtra = [],
  metaIdentifiersExtra = [],
  // optional lifecycle override (pass null to use default buildLifecycle)
  lifecycle: lifecycleProp = null,
}) {
  const navigate = useNavigate();
  const [metaOpen, setMetaOpen] = useState(false);
  const [rawCheckoutOpen, setRawCheckoutOpen] = useState(false);
  const [rawWebhookOpen, setRawWebhookOpen] = useState(false);

  const txnCategory = data.txnCategory || '';
  const status      = data.status || data.txn_status;
  const sc          = status ? statusColors(status) : null;
  const primaryId   = (() => {
    const cat = txnCategory.toUpperCase();
    if (['SETTLE','SETTLEMENT'].includes(cat)) return data.auto_id || data.merchant_ref_id;
    if (['CASHIN','BZCIN'].includes(cat))      return data.request_id;
    if (cat === 'BENE')                        return data.meta?.BENE_UNIQUE_ID || data.beneficiary_id;
    return data.txn_id || data.merchant_ref_id || data.request_id || String(data.id || '');
  })();
  const mainAmt     = data.txn_amount ?? data.amount ?? null;
  const tagPills    = [txnCategory, data.payment_mode || data.meta?.PAYMENT_MODE, data.merchant_type].filter(Boolean);

  const beneficiaryRows = Object.entries(data).filter(([k]) => BENEFICIARY_FIELDS.has(k.toLowerCase()));
  const merchantRows    = Object.entries(data).filter(([k]) => MERCHANT_FIELDS.has(k.toLowerCase()));
  const identifierRows  = Object.entries(data).filter(([k]) => IDENTIFIER_FIELDS.has(k.toLowerCase()));
  const jsonFields      = Object.entries(data).filter(([k, v]) => typeof v === 'object' && v !== null && !Array.isArray(v) && !P2P_OBJ.has(k) && !SYSTEM_FIELDS.has(k) && k !== 'meta');

  const coveredKeys = new Set([...SYSTEM_FIELDS, ...BENEFICIARY_FIELDS, ...MERCHANT_FIELDS, ...IDENTIFIER_FIELDS]);
  const detailRows  = Object.entries(data).filter(([k, v]) => !coveredKeys.has(k.toLowerCase()) && v !== null && v !== undefined && v !== '' && typeof v !== 'object');

  const META_SKIP = new Set(['BANK_RRN', 'ROUTING_ATTEMPTS', 'ORDER_ID', 'ORDERID', 'RAW_RESPONSE', 'RAWRESPONSE', 'PAYMENT_LINK', 'PAYMENTLINK', 'REDIRECTION_URL', 'RAW_CHECKOUT_RESPONSE', 'RAW_WEBHOOK_PAYLOAD']);
  const metaDetailRows = data.meta
    ? Object.entries(data.meta).filter(([k, v]) => !META_SKIP.has(k) && v !== null && v !== undefined && v !== '' && !Array.isArray(v) && typeof v !== 'object')
    : [];

  const quickStats = [
    { label: 'NET AMOUNT',     key: 'net_amount',                  isAmt: true },
    { label: 'MERCHANT COMM.', key: 'merchant_commission_amount',   isAmt: true },
    { label: 'COMPANY COMM.',  key: 'company_commission_amount',    isAmt: true },
  ].filter(({ key }) => data[key] != null);

  const lifecycle = lifecycleProp ?? buildLifecycle(data);

  const parseRawMeta = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch { return raw; }
  };
  const rawCheckoutResponse = parseRawMeta(data.meta?.RAW_CHECKOUT_RESPONSE);
  const rawWebhookPayload   = parseRawMeta(data.meta?.RAW_WEBHOOK_PAYLOAD);

  return (
    <>
      {/* ══ HERO ══ */}
      <Card accent="#4f46e5" style={{ marginBottom: 20 }}>
        <div style={{ ...c.hero, padding: isMobile ? '16px 16px 14px' : '24px 22px 20px', gap: isMobile ? 10 : 24 }}>
          <div style={c.heroLeft}>
            <div style={{ ...c.heroIdRow, gap: 8 }}>
              <span style={{ ...c.txnId, fontSize: isMobile ? 15 : 20 }}>{primaryId}</span>
              <button onClick={() => handleCopy(primaryId)} style={c.copyBtn} title="Copy ID">
                {copied
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                }
              </button>
              {sc && (
                <span style={{ ...c.statusBadge, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontSize: 11, padding: '3px 9px' }}>
                  <span style={{ ...c.dot, background: sc.dot }} />
                  {String(status).charAt(0).toUpperCase() + String(status).slice(1).toLowerCase()}
                </span>
              )}
            </div>
            {tagPills.length > 0 && (
              <div style={{ ...c.tagsRow, gap: 5 }}>
                {tagPills.map((t, i) => <span key={i} style={{ ...c.tag, fontSize: isMobile ? 10.5 : 11.5 }}>{t}</span>)}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {data.created_at && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ ...c.dateLabel, fontSize: isMobile ? 11 : 12.5, flexShrink: 0 }}>Created</span>
                  <span style={{ ...c.dateVal,   fontSize: isMobile ? 11 : 12.5 }}>{fmtDate(data.created_at)}</span>
                </div>
              )}
              {data.updated_at && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ ...c.dateLabel, fontSize: isMobile ? 11 : 12.5, flexShrink: 0 }}>Updated</span>
                  <span style={{ ...c.dateVal,   fontSize: isMobile ? 11 : 12.5 }}>{fmtDate(data.updated_at)}</span>
                </div>
              )}
            </div>
          </div>
          {mainAmt != null && (
            <div style={{ ...c.heroRight, gap: 2 }}>
              <div style={{ ...c.amtLabel, fontSize: isMobile ? 9 : 10 }}>TXN AMOUNT</div>
              <div style={{ ...c.amtValue, fontSize: isMobile ? 22 : 32, letterSpacing: isMobile ? -0.5 : -1.5 }}>₹{fmtAmt(mainAmt)}</div>
              {data.net_amount != null && data.merchant_commission != null && (
                <div style={{ ...c.amtSub, fontSize: isMobile ? 10 : 11.5 }}>Net ₹{fmtAmt(data.net_amount)} + Comm. ₹{fmtAmt(data.merchant_commission)}</div>
              )}
            </div>
          )}
        </div>

        {/* stat chips */}
        {(() => {
          const chips = [
            { label: 'Transaction Type', value: txnCategory || data.payment_mode || data.txn_type, accent: '#4f46e5', icon: <TxnChipIcon /> },
            status && { label: 'Status', value: status, accent: sc?.dot || '#10b981', icon: <StatusChipIcon />, isStatus: true },
            data.response_code != null && { label: 'Response Code', value: String(data.response_code), accent: '#0891b2', icon: <CodeChipIcon /> },
            data.response_message && { label: 'Response Message', value: data.response_message, accent: '#10b981', icon: <MsgChipIcon /> },
          ].filter(Boolean);
          if (!chips.length) return null;
          return (
            <>
              <div style={c.divider} />
              <div style={isMobile
                ? { display: 'flex', overflowX: 'auto', gap: 1, background: '#f1f5f9', scrollbarWidth: 'none' }
                : { display: 'grid', gridTemplateColumns: `repeat(${chips.length}, 1fr)`, gap: 1, background: '#f1f5f9' }}>
                {chips.map((chip, i) => {
                  const csc = chip.isStatus ? statusColors(chip.value) : null;
                  return (
                    <div key={i} className="txn-stat-chip" style={{ ...c.statChipCell, borderTop: `3px solid ${chip.accent}`, ...(isMobile ? { flexShrink: 0, minWidth: 130 } : {}) }}>
                      <div style={{ ...c.statChipIconBox, background: chip.accent + '15', color: chip.accent }}>{chip.icon}</div>
                      <div style={c.statChipLbl}>{chip.label}</div>
                      {chip.isStatus && csc
                        ? <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}><span style={{ ...c.dot, background: csc.dot, width: 7, height: 7 }} /><span style={{ fontSize: 14, fontWeight: 600, color: csc.color }}>{chip.value.charAt(0).toUpperCase() + chip.value.slice(1).toLowerCase()}</span></div>
                        : <div style={{ ...c.statChipVal, color: chip.accent }}>{chip.value}</div>
                      }
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
      </Card>

      {/* No wallet ledger notice */}
      {data.walletLedgerFound === false && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '4px solid #f59e0b', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#92400e' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span><strong>No wallet ledger entry found</strong> — this transaction has not been debited from the wallet yet.</span>
        </div>
      )}

      {/* Merchant nav */}
      {data.merchant_mobile && (
        <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => navigate('/merchant', { state: { mobile: data.merchant_mobile } })} style={c.btnGhost} title="Open merchant details">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ marginRight: 6 }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
            View Merchant
          </button>
        </div>
      )}

      {/* Status check */}
      {showStatusCheck && (
        <>
          <StatusCheckResult
            scResult={scResult} scError={scError} scType={scType}
            scProvider={scProvider} scJsonOpen={scJsonOpen}
            setScJsonOpen={setScJsonOpen} handleCopy={handleCopy} isMobile={isMobile}
          />
          <div style={c.scButtonRow}>
            <button className="btn-status" onClick={onStatusCheck} disabled={scLoading} style={c.btnStatus}>
              {scLoading
                ? <><span style={c.spinner} /> Checking status…</>
                : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ marginRight: 6 }}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.73-4.59"/></svg>Check Status</>
              }
            </button>
          </div>
        </>
      )}

      {/* ══ IDENTIFIERS + MERCHANT ══ */}
      {(identifierRows.length > 0 || metaIdentifiersExtra.length > 0 || merchantRows.length > 0) && (
        <div style={{ ...c.twoCol, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 10 : 18, marginBottom: isMobile ? 10 : 18 }}>
          {(identifierRows.length > 0 || metaIdentifiersExtra.length > 0) && (
            <Card>
              <SectionTitle icon={<IdIcon />} title="Identifiers" />
              <div style={{ padding: '4px 0' }}>
                {identifierRows.map(([k, v], i) => (
                  <KVRow key={k} label={humanLabel(k)} value={v} isMono isLast={i === identifierRows.length - 1 && metaIdentifiersExtra.length === 0} />
                ))}
                {metaIdentifiersExtra.map(([label, value], i) => (
                  <KVRow key={label} label={label} value={value} isMono isLast={i === metaIdentifiersExtra.length - 1} />
                ))}
              </div>
            </Card>
          )}
          {merchantRows.length > 0 && (
            <Card>
              <SectionTitle icon={<MerchantIcon />} title="Merchant Information" />
              <div style={{ padding: '4px 0' }}>
                {merchantRows.map(([k, v], i) => {
                  const key = k.toLowerCase();
                  const isMid     = (key === 'bid' || key === 'mid' || key === 'merchant_id') && v;
                  const isMob     = (key === 'merchant_mobile') && v;
                  const handleClick = isMob
                    ? () => navigate('/merchant', { state: { mobile: String(v) } })
                    : isMid ? () => navigate('/merchant', { state: { username: String(v) } }) : null;
                  return <KVRow key={k} label={humanLabel(k)} value={v} isLast={i === merchantRows.length - 1 && !status} onClick={handleClick} />;
                })}
                {status && <KVRow label="Status" value={status} isStatus isLast />}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ══ BENEFICIARY ══ */}
      {(beneficiaryRows.length > 0 || metaBeneExtra.length > 0) && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon={<BeneIcon />} title="Beneficiary Details" />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 0 }}>
            {beneficiaryRows.map(([k, v]) => (
              <div key={k} className="txn-kv-row" style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc' }}>
                <span style={c.kvLabel}>{humanLabel(k)}</span>
                <span style={c.kvVal}>{fmtVal(v)}</span>
              </div>
            ))}
            {metaBeneExtra.map(([label, value]) => (
              <div key={label} className="txn-kv-row" style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc' }}>
                <span style={c.kvLabel}>{label}</span>
                <span style={{ ...c.kvVal, fontFamily: ['IFSC Code','Bank RRN'].includes(label) ? 'monospace' : 'inherit', fontSize: ['IFSC Code','Bank RRN'].includes(label) ? 12 : 13 }}>{fmtVal(value)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ══ TRANSACTION DETAILS ══ */}
      {(detailRows.length > 0 || metaDetailRows.length > 0 || data.meta?.BANK_RRN || data.txn_amount != null || data.net_amount != null || data.txn_status) && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon={<DetailIcon />} title="Transaction Details" />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 0 }}>
            {data.meta?.BANK_RRN && (
              <div className="txn-kv-row" style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc' }}>
                <span style={c.kvLabel}>UTR</span>
                <span style={{ ...c.kvVal, fontFamily: 'monospace', fontSize: 12 }}>{data.meta.BANK_RRN}</span>
              </div>
            )}
            {data.txn_amount != null && (
              <div className="txn-kv-row" style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc' }}>
                <span style={c.kvLabel}>Txn Amount</span>
                <span style={c.kvVal}>₹{fmtAmt(data.txn_amount)}</span>
              </div>
            )}
            {data.net_amount != null && (
              <div className="txn-kv-row" style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc' }}>
                <span style={c.kvLabel}>Net Amount</span>
                <span style={c.kvVal}>₹{fmtAmt(data.net_amount)}</span>
              </div>
            )}
            {data.txn_status && (() => {
              const sc3 = statusColors(data.txn_status);
              return (
                <div className="txn-kv-row" style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc' }}>
                  <span style={c.kvLabel}>Txn Status</span>
                  <span style={{ ...c.statusBadge, background: sc3.bg, color: sc3.color, border: `1px solid ${sc3.border}` }}><span style={{ ...c.dot, background: sc3.dot }} />{data.txn_status}</span>
                </div>
              );
            })()}
            {detailRows.map(([k, v]) => {
              const isSt = k.endsWith('_status') || k === 'settle_status';
              const isOp = k === 'operator';
              const opName = isOp ? OPERATOR_MAP[String(v).toUpperCase()] : null;
              return (
                <div key={k} className="txn-kv-row" style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc' }}>
                  <span style={c.kvLabel}>{humanLabel(k)}</span>
                  {isSt
                    ? (() => { const sc3 = statusColors(String(v)); return <span style={{ ...c.statusBadge, background: sc3.bg, color: sc3.color, border: `1px solid ${sc3.border}` }}><span style={{ ...c.dot, background: sc3.dot }} />{String(v)}</span>; })()
                    : isOp
                      ? <span style={c.kvVal}>{opName ? `${opName} (${v})` : fmtVal(v)}</span>
                      : <span style={c.kvVal}>{fmtVal(v)}</span>
                  }
                </div>
              );
            })}
            {metaDetailRows.map(([k, v]) => {
              const isUrl  = typeof v === 'string' && v.startsWith('http');
              const isAmt  = ['FEES', 'SETTLEMENT_AMOUNT', 'GST', 'NET_AMOUNT'].includes(k);
              const isCaps = ['RRN', 'UTR', 'BANK_RRN'].includes(k);
              const isMono = isCaps || ['ORDER_ID', 'PAYMENT_REF_ID'].includes(k) || isUrl;
              const isLong = typeof v === 'string' && v.length > 80;
              return (
                <div key={k} className="txn-kv-row"
                  style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc', ...(isLong ? { gridColumn: '1 / -1' } : {}) }}>
                  <span style={c.kvLabel}>{humanLabel(k)}</span>
                  {isUrl
                    ? <a href={v} target="_blank" rel="noopener noreferrer"
                        style={{ ...c.kvVal, color: '#4f46e5', fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{v}</a>
                    : <span style={{ ...c.kvVal, fontFamily: isMono ? 'monospace' : 'inherit', fontSize: isMono ? 12 : 13,
                        ...(isLong ? { wordBreak: 'break-all', fontSize: 11, color: '#475569', fontFamily: 'monospace' } : {}) }}>
                        {isAmt ? `₹${fmtAmt(v)}` : isCaps ? String(v).toUpperCase() : fmtVal(v)}
                      </span>
                  }
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ══ QUICK STATS ══ */}
      {quickStats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${quickStats.length}, 1fr)`, gap: isMobile ? 10 : 14, marginBottom: isMobile ? 10 : 20 }}>
          {quickStats.map(({ label, key, isAmt }) => {
            const val = data[key]; const num = Number(val);
            return (
              <Card key={key} accent="#4f46e5">
                <div className="txn-card-inner" style={{ padding: '20px 22px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: isAmt ? (num === 0 ? '#94a3b8' : '#10b981') : '#0f172a', fontFamily: isAmt ? 'monospace' : 'inherit', letterSpacing: isAmt ? -0.5 : 0 }}>
                    {isAmt ? `₹${fmtAmt(val)}` : String(val)}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ══ COMMISSIONS ══ */}
      {data.commissions?.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon={<CommIcon />} title="Commissions" right={<span style={c.countBadge}>{data.commissions.length}</span>} />
          <div className="txn-comm-inner" style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.commissions.map((cm, i) => {
              const rc = { MERCHANT: ['#4f46e5','#e0e7ff'], DISTRIBUTOR: ['#7c3aed','#ede9fe'], RETAILER: ['#059669','#d1fae5'] }[cm.role?.toUpperCase()] || ['#64748b','#f1f5f9'];
              return (
                <div key={i} style={{ ...c.commCard, borderLeft: `4px solid ${rc[0]}` }}>
                  <div style={c.commTop}>
                    <div style={c.commName}>{cm.name || cm.bid}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {cm.role && <span style={{ ...c.rolePill, background: rc[1], color: rc[0] }}>{cm.role}</span>}
                      {cm.mobile && <span onClick={() => navigate('/merchant', { state: { mobile: String(cm.mobile) } })} style={{ ...c.commMobile, color: '#4f46e5', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>{cm.mobile}</span>}
                    </div>
                  </div>
                  <div style={isMobile
                    ? { display: 'flex', overflowX: 'auto', gap: 1, background: '#f1f5f9', scrollbarWidth: 'none' }
                    : { display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 1, background: '#f1f5f9' }}>
                    {[
                      ['BID', cm.bid, false], ['COMMISSION', `₹${fmtAmt(cm.txn_amount)}`, true, '#10b981'],
                      ['PRE BAL.', `₹${fmtAmt(cm.pre_txn_balance)}`, false], ['POST BAL.', `₹${fmtAmt(cm.post_txn_balance)}`, false],
                      ['CR / DR', cm.cr_dr, false, cm.cr_dr === 'CR' ? '#10b981' : '#ef4444'],
                    ].map(([lbl, val, bold, color]) => (
                      <div key={lbl} style={{ background: '#fff', padding: '10px 14px', ...(isMobile ? { flexShrink: 0, minWidth: 90 } : {}) }}>
                        <div style={c.miniLabel}>{lbl}</div>
                        {lbl === 'BID' && cm.bid
                          ? <div onClick={() => navigate('/merchant', { state: { username: String(cm.bid) } })} style={{ fontSize: 13, fontWeight: 600, color: '#4f46e5', fontFamily: 'monospace', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>{val}</div>
                          : <div style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: color || '#0f172a', fontFamily: 'monospace' }}>{val}</div>
                        }
                      </div>
                    ))}
                  </div>
                  {cm.txn_category_txn_id && <div style={{ padding: '7px 16px', fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', borderTop: '1px solid #f8fafc', wordBreak: 'break-all' }}>{cm.txn_category_txn_id}</div>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ══ REFUND ══ */}
      {data.refund && (
        <Card style={{ marginBottom: 20 }} accent="#8b5cf6">
          <SectionTitle icon={<RefundIcon />} title="Refund" />
          <div className="txn-comm-inner" style={{ padding: '12px 20px' }}>
            <div style={{ ...c.commCard, borderLeft: '4px solid #8b5cf6' }}>
              <div style={c.commTop}>
                <div style={c.commName}>{data.refund.name || data.refund.bid}</div>
                {data.refund.role && <span style={{ ...c.rolePill, background: '#f5f3ff', color: '#7c3aed' }}>{data.refund.role}</span>}
              </div>
              <div style={isMobile
                ? { display: 'flex', overflowX: 'auto', gap: 1, background: '#f1f5f9', scrollbarWidth: 'none' }
                : { display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 1, background: '#f1f5f9' }}>
                {[
                  ['BID', data.refund.bid, false], ['AMOUNT', `₹${fmtAmt(data.refund.txn_amount)}`, true, '#10b981'],
                  ['CR/DR', data.refund.cr_dr, false, data.refund.cr_dr === 'CR' ? '#10b981' : '#ef4444'],
                  ['PRE BAL.', `₹${fmtAmt(data.refund.pre_txn_balance)}`, false], ['POST BAL.', `₹${fmtAmt(data.refund.post_txn_balance)}`, false],
                  ['STATUS', data.refund.status, false],
                ].map(([lbl, val, bold, color]) => (
                  <div key={lbl} style={{ background: '#fff', padding: '10px 14px', ...(isMobile ? { flexShrink: 0, minWidth: 90 } : {}) }}>
                    <div style={c.miniLabel}>{lbl}</div>
                    {lbl === 'BID' && data.refund.bid
                      ? <div onClick={() => navigate('/merchant', { state: { username: String(data.refund.bid) } })} style={{ fontSize: 13, fontWeight: 600, color: '#4f46e5', fontFamily: 'monospace', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>{val}</div>
                      : <div style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: color || '#0f172a', fontFamily: 'monospace' }}>{val}</div>
                    }
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ══ WALLET LEDGER ══ */}
      {data.walletLedger?.length > 0 && (
        <Card style={{ marginBottom: 20 }} accent="#8b5cf6">
          <div className="txn-comm-inner" style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.walletLedger.map((row, i) => {
              const isCr = row.cr_dr === 'CR';
              return (
                <div key={i} style={{ ...c.commCard, borderLeft: '4px solid #8b5cf6' }}>
                  <div style={{ ...c.commTop, gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', fontFamily: 'monospace' }}>{row.txn_category_txn_id}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', padding: '2px 8px', borderRadius: 20 }}>REFUND</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ ...c.statusBadge, background: isCr ? '#ecfdf5' : '#fef2f2', color: isCr ? '#059669' : '#dc2626', border: `1px solid ${isCr ? '#a7f3d0' : '#fecaca'}`, fontSize: 11 }}>
                        <span style={{ ...c.dot, background: isCr ? '#10b981' : '#ef4444' }} />{row.cr_dr}
                      </span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : ''}</span>
                    </div>
                  </div>
                  <div style={isMobile
                    ? { display: 'flex', overflowX: 'auto', gap: 1, background: '#f1f5f9', scrollbarWidth: 'none' }
                    : { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, background: '#f1f5f9' }}>
                    <div style={{ background: '#fff', padding: '10px 14px', ...(isMobile ? { flexShrink: 0, minWidth: 100 } : {}) }}>
                      <div style={c.miniLabel}>BID</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', fontFamily: 'monospace' }}>{row.bid}</div>
                      {row.merchant_name && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{row.merchant_name}</div>}
                    </div>
                    {[
                      ['TXN AMOUNT', `₹${Number(row.txn_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, true, '#8b5cf6'],
                      ['PRE BAL.',   `₹${Number(row.pre_txn_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, false, null],
                      ['POST BAL.',  `₹${Number(row.post_txn_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, false, null],
                      ['STATUS',     row.status, false, null],
                    ].map(([lbl, val, bold, color]) => (
                      <div key={lbl} style={{ background: '#fff', padding: '10px 14px', ...(isMobile ? { flexShrink: 0, minWidth: 100 } : {}) }}>
                        <div style={c.miniLabel}>{lbl}</div>
                        <div style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: color || '#0f172a', fontFamily: 'monospace' }}>{val || '—'}</div>
                      </div>
                    ))}
                  </div>
                  {row.parent_id && <div style={{ padding: '7px 16px', fontSize: 11, color: '#7c3aed', fontFamily: 'monospace', borderTop: '1px solid #f8fafc', display: 'flex', gap: 8 }}><span style={{ color: '#94a3b8' }}>parent_id:</span>{row.parent_id}</div>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ══ P2P ══ */}
      {txnCategory === 'P2P' && (data.sender || data.receiver) && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon={<P2PIcon />} title="P2P Transfer" />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1, background: '#f1f5f9' }}>
            {[['sender', '▼ SENDER', '#ef4444', data.sender_name], ['receiver', '▲ RECEIVER', '#10b981', data.receiver_name]].map(([key, label, color, name]) => (
              <div key={key} style={{ background: '#fff' }}>
                <div className="txn-section-title" style={{ padding: '12px 20px', fontSize: 11, fontWeight: 700, color, letterSpacing: 1, borderBottom: '1px solid #f8fafc' }}>{label}</div>
                {[['Name', name], ['MID', data[key]?.bid],
                  ['Amount', data[key]?.txn_amount != null ? `₹${Number(data[key].txn_amount).toFixed(2)}` : null],
                  ['CR/DR', data[key]?.cr_dr], ['Pre Bal.', data[key]?.pre_txn_balance != null ? `₹${Number(data[key].pre_txn_balance).toFixed(2)}` : null],
                  ['Post Bal.', data[key]?.post_txn_balance != null ? `₹${Number(data[key].post_txn_balance).toFixed(2)}` : null],
                ].filter(([,v]) => v != null).map(([lbl, val], i) => (
                  <div key={i} className="txn-kv-row" style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc' }}>
                    <span style={c.kvLabel}>{lbl}</span><span style={c.kvVal}>{val}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ══ AEPS SETTLE TRANSFER ══ */}
      {txnCategory === 'AEPS_SETTLE' && (data.debit || data.credit) && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon={<P2PIcon />} title="Settlement Transfer" />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1, background: '#f1f5f9' }}>
            {[
              ['debit',  '▼ DEBIT — AEPS Wallet',  '#ef4444', data.debit],
              ['credit', '▲ CREDIT — Main Wallet', '#10b981', data.credit],
            ].map(([key, label, color, entry]) => entry && (
              <div key={key} style={{ background: '#fff' }}>
                <div className="txn-section-title" style={{ padding: '12px 20px', fontSize: 11, fontWeight: 700, color, letterSpacing: 1, borderBottom: '1px solid #f8fafc' }}>{label}</div>
                {[
                  ['Txn ID',       entry.txn_category_txn_id],
                  ['MID',          entry.bid],
                  ['Name',         entry.merchant_name],
                  ['Mobile',       entry.merchant_mobile],
                  ['Amount',       entry.txn_amount   != null ? `₹${fmtAmt(entry.txn_amount)}`        : null],
                  ['Pre Balance',  entry.pre_txn_balance  != null ? `₹${fmtAmt(entry.pre_txn_balance)}`  : null],
                  ['Post Balance', entry.post_txn_balance != null ? `₹${fmtAmt(entry.post_txn_balance)}` : null],
                  ['CR / DR',      entry.cr_dr],
                  ['Status',       entry.status],
                  ['Created',      entry.created_at ? fmtDate(entry.created_at) : null],
                ].filter(([, v]) => v != null).map(([lbl, val], i) => (
                  <div key={i} className="txn-kv-row" style={{ ...c.kvRow, borderBottom: '1px solid #f8fafc' }}>
                    <span style={c.kvLabel}>{lbl}</span>
                    <span style={{
                      ...c.kvVal,
                      ...(lbl === 'CR / DR' ? { color: val === 'CR' ? '#059669' : '#dc2626', fontWeight: 700 } : {}),
                      ...(lbl === 'Txn ID' || lbl === 'MID' ? { fontFamily: 'monospace', fontSize: 12 } : {}),
                    }}>{val}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ══ LIFECYCLE ══ */}
      {lifecycle.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon={<LifecycleIcon />} title="Lifecycle" />
          <div style={c.lifecycle}>
            {lifecycle.map((step, i) => (
              <div key={i} style={c.lcStep}>
                <div style={c.lcTopRow}>
                  <div style={{ ...c.lcCircle, background: step.done ? step.color : '#e2e8f0', boxShadow: step.done ? `0 0 0 4px ${step.color}22` : 'none' }}>
                    {step.done
                      ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                      : <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#cbd5e1' }} />
                    }
                  </div>
                  {i < lifecycle.length - 1 && <div style={{ ...c.lcLine, background: step.done ? `linear-gradient(90deg, ${step.color}, ${lifecycle[i+1].color})` : '#e2e8f0' }} />}
                </div>
                <div style={c.lcContent}>
                  <div style={{ ...c.lcTitle, color: step.done ? '#0f172a' : '#94a3b8' }}>{step.title}</div>
                  <div style={c.lcDesc}>{step.desc}</div>
                  {step.sub && <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 3, fontFamily: 'monospace', wordBreak: 'break-all' }}>{step.sub}</div>}
                  {step.time && (
                    <div style={c.lcTime}>
                      {(() => { try { const d = new Date(step.time); return <>{d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}<br />{d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</>; } catch { return step.time; } })()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ══ ROUTING ATTEMPTS ══ */}
      {data.meta?.ROUTING_ATTEMPTS?.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle icon={<RoutingIcon />} title="Routing Attempts" right={<span style={c.countBadge}>{data.meta.ROUTING_ATTEMPTS.length}</span>} />
          <div className="txn-comm-inner" style={{ padding: '12px 20px' }}>
            <RoutingAttempts meta={data.meta} onCopy={handleCopy} />
          </div>
        </Card>
      )}

      {/* ══ PROVIDER METADATA ══ */}
      {jsonFields.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <div className="txn-section-title" style={{ ...c.sectionTitle, cursor: 'pointer' }} onClick={() => setMetaOpen(o => !o)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={c.sectionIcon}><MetaIcon /></div>
              <span style={c.sectionTitleText}>Provider Metadata</span>
            </div>
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{metaOpen ? '▲ Hide' : '▼ Show'}</span>
          </div>
          {metaOpen && jsonFields.map(([key, value]) => (
            <div key={key} style={c.codeBlock}>
              <div style={c.codeHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={c.codeSquare} /><span style={c.codeLabel}>RAW PAYLOAD · JSON</span></div>
                <button style={c.codeCopyBtn} onClick={() => handleCopy(JSON.stringify(value, null, 2))}>COPY</button>
              </div>
              <pre style={c.codePre} dangerouslySetInnerHTML={{ __html: highlight(value) }} />
            </div>
          ))}
        </Card>
      )}

      {/* ══ RAW CHECKOUT RESPONSE ══ */}
      {rawCheckoutResponse && (
        <Card style={{ marginBottom: 20 }}>
          <div className="txn-section-title" style={{ ...c.sectionTitle, cursor: 'pointer' }} onClick={() => setRawCheckoutOpen(o => !o)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={c.sectionIcon}><MetaIcon /></div>
              <span style={c.sectionTitleText}>Raw Checkout Response</span>
            </div>
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{rawCheckoutOpen ? '▲ Hide' : '▼ Show'}</span>
          </div>
          {rawCheckoutOpen && (
            <div style={{ padding: 16 }}>
              <div style={c.codeBlock}>
                <div style={c.codeHeader}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={c.codeSquare} /><span style={c.codeLabel}>RAW CHECKOUT RESPONSE · JSON</span></div>
                  <button style={c.codeCopyBtn} onClick={() => handleCopy(JSON.stringify(rawCheckoutResponse, null, 2))}>COPY</button>
                </div>
                <pre style={c.codePre} dangerouslySetInnerHTML={{ __html: highlight(rawCheckoutResponse) }} />
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ══ RAW WEBHOOK PAYLOAD ══ */}
      {rawWebhookPayload && (
        <Card style={{ marginBottom: 20 }}>
          <div className="txn-section-title" style={{ ...c.sectionTitle, cursor: 'pointer' }} onClick={() => setRawWebhookOpen(o => !o)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={c.sectionIcon}><MetaIcon /></div>
              <span style={c.sectionTitleText}>Raw Webhook Payload</span>
            </div>
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{rawWebhookOpen ? '▲ Hide' : '▼ Show'}</span>
          </div>
          {rawWebhookOpen && (
            <div style={{ padding: 16 }}>
              <div style={c.codeBlock}>
                <div style={c.codeHeader}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={c.codeSquare} /><span style={c.codeLabel}>RAW WEBHOOK PAYLOAD · JSON</span></div>
                  <button style={c.codeCopyBtn} onClick={() => handleCopy(JSON.stringify(rawWebhookPayload, null, 2))}>COPY</button>
                </div>
                <pre style={c.codePre} dangerouslySetInnerHTML={{ __html: highlight(rawWebhookPayload) }} />
              </div>
            </div>
          )}
        </Card>
      )}
    </>
  );
}
